import Button from '@/components/Button';
import Input from '@/components/Input';
import LogContextModal from '@/components/modals/LogContextModal';
import Note from '@/components/Note';
import { BiCollapse, BiExpand, FiFileText, FiSearch, FiTable, FiX, IoEllipsisVertical, IoMdCloudDownload, MdContentCopy, TbClipboardCopy, TbListDetails } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import ScreenBreadcrumbV2 from '@/components/v2/ScreenBreadcrumbV2';
import { useAnalytics } from '@/hooks/useAnalytics';
import { action } from '@/lib/api';
import clsx from '@/lib/clsx';
import confirmDialog from '@/lib/confirmDialog';
import { DeleteIcon } from '@/lib/constants/icons';
import copyToClipboard from '@/lib/copyToClipboard';
import { deleteWorkflowStepResponse, getStepActionMockData, getStepActionResponses } from '@/redux/actions/gravityActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import { warningSchema } from '@shared/schema/warningSchema.js';
import { toSlugCase } from '@shared/utils/toSlugCase.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { randomId } from 'shared/utils/randomId';
import workflowScreenSelectors from '../redux/selectors/workflowScreenSelectors';

const warningCodes = ['API_RESPONSE_MAPPER_INVALID_DATA_FORMAT', 'API_RESPONSE_MAPPER_INVALID_HTTP_STATUS', 'NO_CHILD_FOUND', 'FLOW_CONTROL_EMAIL_FAILED'];

const noop = () => {};

// We need stepId as a prop because if you try to get it in logs, you get log id instead.

const WorkflowOutputPaneUtilities = ({ search = '', onChange, output, stepId, breadcrumb, isLog, activeTab, responseCollapseAmount, onResponseCollapse, allStepIterations }) => {
  const safeOnChange = typeof onChange === 'function' ? onChange : noop;
  if (onChange != null && typeof onChange !== 'function') {
    console.warn('WorkflowOutputPaneUtilities: onChange prop is not a function', { onChange, stepId });
  }
  const [searchVisible, setSearchVisible] = useState(false);
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const workflowVersion = useArgSelector(selectors.param, 'workflowVersion');
  const appSlug = useSelector((state) => workflowScreenSelectors.activeWorkflowStepAppByStepId(state, stepId)?.slug);
  const stepActionId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepById(state, stepId)?.actionId);
  const stepFunctionId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepById(state, stepId)?.functionId);
  const stepMessage = useSelector((state) => workflowScreenSelectors.activeWorkflowStepById(state, stepId)?.message);
  const isLoading = useSelector((state) => state?.workflow?.stepState?.[stepId]?.status === 'loading');
  const admin = useSelector(selectors.admin);
  const moreOptionsTriggerId = useRef(randomId());
  const searchFocusTimeoutRef = useRef(null);
  const { trackEvent } = useAnalytics();

  const isMock = output?.id === 'mock';
  const isIterate = appSlug === 'iterate';
  const requestData = { curl: output?.meta?.curl, output: output?.meta?.output, baseURL: output?.meta?.response?.config?.baseURL, url: output?.meta?.response?.config?.url, headers: output?.meta?.response?.config?.headers };
  const warnings = output?.meta?.warnings?.filter((warning) => warningCodes.includes(warning.code)).map((warning) => ({ type: 'warning', schema: warningSchema[warning.code] }));
  const showTable = output?.data?.length > 0;
  const showContext = admin && output?.meta?.context?.length > 0;
  const filename = toSlugCase(stepMessage);

  const handleDeleteOutputButtonClick = async () => {
    const confirm = await confirmDialog({
      title: 'Are you sure you want to delete this output?',
      message: 'This cannot be undone.'
    });
    if (confirm === true) {
      trackEvent('workflow-log-deleteOutput');
      deleteWorkflowStepResponse({
        workflowId,
        stepId,
        actionId: stepActionId,
        callback: () => {
          action('SET_STEP_STATE', { [stepId]: { status: 'loading' } });
          getStepActionMockData({ workflowId, workflowVersion, stepId, actionId: stepActionId }, () => action('SET_STEP_STATE', { [stepId]: null }));
        }
      });
    }
  };

  const handleCopyCurlToClipboard = (curl) => {
    trackEvent('workflow-log-copyCurl');
    // Only escape single quotes in JSON body
    const sanitizedCurl = curl.replace(/--data '({[\s\S]*?})'/g, (match, body) => {
      return `--data '${body.replace(/'/g, "'\\''")}'`;
    });
    copyToClipboard(sanitizedCurl, { toast: true });
  };

  const handleViewContext = () => {
    setModalState('logContext', { open: true, context: output?.meta?.context });
  };

  const copyOutputDataToClipboard = () => {
    copyToClipboard(JSON.stringify(output?.data, null, 2), { toast: true });
  };

  const handleResponseCollapse = () => {
    const newCollapseAmount = responseCollapseAmount === false ? 3 : false;
    onResponseCollapse(newCollapseAmount);
  };

  const toggleSearch = () => {
    if (searchFocusTimeoutRef.current) {
      clearTimeout(searchFocusTimeoutRef.current);
      searchFocusTimeoutRef.current = null;
    }

    if (!searchVisible) {
      searchFocusTimeoutRef.current = setTimeout(() => {
        if (typeof document === 'undefined') {
          return;
        }

        document.querySelector('.output-pane-search .comp-input input')?.focus();
        searchFocusTimeoutRef.current = null;
      }, 100);
    } else {
      safeOnChange('');
    }
    setSearchVisible((prev) => !prev);
  };

  useEffect(() => {
    return () => {
      if (searchFocusTimeoutRef.current) {
        clearTimeout(searchFocusTimeoutRef.current);
        searchFocusTimeoutRef.current = null;
      }
    };
  }, []);

  const downloadAsCsv = (rawData) => {
    const blob = new Blob([rawData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = () => {
    const payload = {
      workflowId,
      stepId,
      actionId: stepActionId,
      format: 'csv',
      outputPage: 'all'
    };
    trackEvent('workflow-log-downloadCsv');
    getStepActionResponses(payload, downloadAsCsv);
  };

  const downloadAsJson = (rawData) => {
    const jsonString = typeof rawData === 'string' ? rawData : JSON.stringify(rawData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadJson = () => {
    const payload = {
      workflowId,
      stepId,
      actionId: stepActionId,
      format: 'json',
      outputPage: 'all'
    };
    trackEvent('workflow-log-downloadJson');
    getStepActionResponses(payload, downloadAsJson);
  };

  const handleViewAsTable = () => {
    setModalState('table', { open: true, output });
  };

  const handleViewFunctionLog = () => {
    const url = `/workflows/${stepFunctionId}/draft?outputType=live&logs=true&logId=${output?.meta?.functionLogId}`;
    window.open(url, '_blank');
  };

  const handleDownloadAllIterations = () => {
    if (!Array.isArray(allStepIterations) || allStepIterations.length === 0) {
      return;
    }

    // Collect all data from all iterations into a single array
    const allData = allStepIterations.flatMap((iteration) => iteration.data || []);

    // Create JSON payload with metadata
    const payload = {
      stepId,
      totalIterations: allStepIterations.length,
      totalRecords: allData.length,
      iterations: allStepIterations.map((iteration, index) => ({
        iteration: index + 1,
        stepId: iteration.stepId,
        success: iteration.response?.success,
        data: iteration.data,
        meta: iteration.response?.meta
      })),
      data: allData
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-all-iterations.json`;
    a.click();
    window.URL.revokeObjectURL(url);

    trackEvent('workflow-log-downloadAllIterations');
  };

  const fullOptions = useMemo(() => {
    const opts = [
      {
        value: 'collapse-expand',
        label: responseCollapseAmount === false ? 'Collapse all' : 'Expand all',
        icon: responseCollapseAmount === false ? BiCollapse : BiExpand,
        onChange: handleResponseCollapse,
        hide: activeTab !== '5'
      },
      {
        value: 'view-as-table',
        label: 'View as table',
        icon: FiTable,
        onChange: handleViewAsTable,
        hide: !showTable
      },
      {
        value: 'view-function-log',
        label: 'View function log',
        icon: FiFileText,
        onChange: handleViewFunctionLog,
        hide: !output?.meta?.functionLogId
      },
      {
        value: 'copy-output',
        label: 'Copy output',
        icon: MdContentCopy,
        onChange: copyOutputDataToClipboard,
        hide: output?.success !== true
      },
      {
        value: 'copy-curl',
        label: 'Copy cURL',
        icon: TbClipboardCopy,
        onChange: () => handleCopyCurlToClipboard(requestData.curl),
        hide: !requestData.curl
      },
      {
        value: 'download-csv',
        label: 'Download as CSV',
        icon: IoMdCloudDownload,
        onChange: handleDownloadCsv,
        hide: isLog
      },
      {
        value: 'download-json',
        label: 'Download as JSON',
        icon: IoMdCloudDownload,
        onChange: handleDownloadJson,
        hide: isLog
      },
      {
        value: 'download-all-iterations',
        label: `Download all iterations (${allStepIterations?.length || 0})`,
        icon: IoMdCloudDownload,
        onChange: handleDownloadAllIterations,
        hide: !isLog || !allStepIterations || allStepIterations.length <= 1
      },
      {
        value: 'delete',
        label: 'Delete this output',
        icon: DeleteIcon,
        onChange: handleDeleteOutputButtonClick,
        hide: false
      }
    ].filter((n) => !n.hide);

    return opts.map((item) => ({ ...item, iconProps: { className: 'light-medium', size: 12 } }));
  }, [activeTab, responseCollapseAmount, output, requestData.curl, isLog, allStepIterations]);

  return (
    <>
      {output?.meta && !isMock && !isIterate && (
        <div className={clsx('output-pane-utilities-container', isLog && 'log')}>
          {!searchVisible && <ScreenBreadcrumbV2 stack={breadcrumb} className="output-pane-breadcrumb" />}
          <div className={`output-pane-search ${searchVisible ? 'visible' : ''}`}>
            <Button icon={searchVisible && isLoading ? Spinner : FiSearch} size="sm" className="ghost search-btn" onClick={toggleSearch} />
            <Input placeholder="Search" size="sm" className="borderless" value={search} onChange={(e) => safeOnChange(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && toggleSearch()} />
            {searchVisible && <Button icon={FiX} size="xs" className="ghost close-btn" onClick={toggleSearch} />}
          </div>
          {showContext && (
            <>
              <Button icon={TbListDetails} size="sm" className="ghost" title={'View context'} onClick={handleViewContext} />
              <LogContextModal />
            </>
          )}
          {fullOptions.length > 0 && (
            <>
              <Button className={`ghost ${moreOptionsTriggerId.current}`} size="sm" icon={IoEllipsisVertical} />
              <FloatingSelectV2 triggers={[`.${moreOptionsTriggerId.current}`]} options={fullOptions} useSearch={false} />
            </>
          )}
        </div>
      )}
      {admin && warnings?.map((warning, index) => <Note key={index} type={warning.type} text={warning.schema.description} link={warning.schema.link} size={'sm'} />)}
    </>
  );
};

export default WorkflowOutputPaneUtilities;
